import {useMemo, useRef, useState} from "react";
import {makeStyles, TableCell, TableRow, Typography} from "@material-ui/core";
import Tr from "translations/Translations";
import clsx from "clsx";
import OfferChoiceContractorRowPart from "./OfferChoiceContractorRowPart";
import BoldTypography from "components/BoldTypography";
import OfferChoiceSummaryCell from "./OfferChoiceSummaryCell";
import {RoundType} from "constants/RoundType";
import {AdditionalRoundStatusesPending} from "constants/AdditionalRoundStatus";
import OfferChoiceSummaryCellAdditionalRound from "./additionalRound/OfferChoiceSummaryCellAdditionalRound";
import PropTypes from 'prop-types';
import OfferChoiceSummaryCellAuction from "./auction/OfferChoiceSummaryCellAuction";
import moment from "libs/moment";
import OfferChoiceService from "services/OfferChoiceService";
import {isOfferChoiceStateWithDecision} from "../constants/OfferChoiceState";

const useStyles = makeStyles(theme => ({
    italic: {
        fontStyle: 'italic',
        userSelect: 'none'
    },
    evenRow: {
        backgroundColor: theme.palette.background.default
    },
    lastRow: {
        borderBottom: `solid 3px ${theme.palette.primary.main}`
    },
    choiceSize: {
        "& .MuiInput-root": {
            fontSize: theme.typography.body2.fontSize,
            color: theme.palette.text.primary
        }
    },
    choiceStatus: {
        marginBottom: theme.spacing()
    },
    choiceControls: {
        marginTop: theme.spacing(2),
        textAlign: 'center'
    },
    choiceControlsEnd: {
        textAlign: 'end'
    },
    noOffer: {
        textAlign: 'center',
        fontSize: '1.5rem'
    }
}));

function OfferChoiceRow(
    {
        demandId,
        editable,
        working,
        offerChoiceRow,
        rowNumber,
        updateStatus,
        modifyChoice,

    }) {

    const classes = useStyles();
    const even = rowNumber % 2 === 1;
    const serverBrowserTimeDiff = useRef(moment(offerChoiceRow.serverTime).diff(moment(), "s"));

    const [chosenSuppliers, setChosenSuppliers] = useState(offerChoiceRow.offerChoices.filter(x => x.chosen).map(x => x.identity));
    const [chosenStatusDone, setChosenStatusDone] = useState(isOfferChoiceStateWithDecision(offerChoiceRow.state));
    const [curWorking, setCurWorking] = useState(false);
    const maxPartScore = useMemo(() => offerChoiceRow.offerChoices.reduce((prev, cur) => prev < cur.offerScore ? cur.offerScore : prev, 0), [offerChoiceRow]);

    const refreshRow = async (sectionsToRefresh) => {
        setCurWorking(true);
        await OfferChoiceService.updatePosition(demandId, offerChoiceRow.identity, sectionsToRefresh).finally(() => setCurWorking(false));
    };

    const chooseSupplier = (supplier) => {
        const index = chosenSuppliers.indexOf(supplier.identity);
        if (index > -1) {
            setChosenSuppliers(chosenSuppliers.filter(x => x !== supplier.identity));
        } else {
            if (chosenSuppliers.length < offerChoiceRow.maxOfferChoiceCount) {
                setChosenSuppliers([...chosenSuppliers, supplier.identity]);
            }
        }
    }

    const doUpdateStatus = async (positionId, canceled, chosenSupplierIds, offerChoiceDate, offerChoiceJustification) => {
        setCurWorking(true);
        try {
            await updateStatus(positionId, canceled, chosenSupplierIds, offerChoiceDate, offerChoiceJustification);

        } finally {
            setCurWorking(false);
        }
    }

    const doModifyChoice = async (positionId) => {
        setCurWorking(true);
        try {
            await modifyChoice(positionId);
        } finally {
            setCurWorking(false);
        }
    }

    const isAdditionalOffers = useMemo(() => !!offerChoiceRow.currentAdditionalRoundInfo &&
        offerChoiceRow.currentAdditionalRoundInfo.type === RoundType.ADDITIONAL_OFFERS &&
        AdditionalRoundStatusesPending.indexOf(offerChoiceRow.currentAdditionalRoundInfo.status) > -1
        , [offerChoiceRow]);

    const isAuction = useMemo(() => !!offerChoiceRow.currentAdditionalRoundInfo &&
        offerChoiceRow.currentAdditionalRoundInfo.type === RoundType.AUCTION &&
        AdditionalRoundStatusesPending.indexOf(offerChoiceRow.currentAdditionalRoundInfo.status) > -1
        , [offerChoiceRow]);

    const disabledInputs = working || chosenStatusDone || !editable || curWorking || isAdditionalOffers || isAuction;

    const getRow = (element, index, array) => {
        const noOffer = element.identity === -1;
        const first = index === 0;
        const last = index >= array.length - 1;

        const rowSpan = noOffer ? 1 : array.length;
        return (<TableRow key={element.identity} className={clsx({[classes.evenRow]: even, [classes.lastRow]: last})}>
            {first && <TableCell className={classes.partColumn} rowSpan={rowSpan}>
                <Typography className={classes.italic} variant="body2">
                    {Tr(`offer_evaluation_mode_title_position`, {value: offerChoiceRow.order})}
                </Typography>
                <Typography variant="body2">
                    {offerChoiceRow.name}
                </Typography>
            </TableCell>}

            {noOffer ?
                <TableCell className={classes.noOffer} colSpan={4}>
                    <BoldTypography>{Tr('offer_status_noOffer')}</BoldTypography>
                </TableCell>
                :
                <OfferChoiceContractorRowPart
                    editable={!disabledInputs}
                    chosen={chosenSuppliers.indexOf(element.identity) > -1}
                    chooseSupplierCountReached={chosenSuppliers.length >= offerChoiceRow.maxOfferChoiceCount}
                    offerChoiceContractor={element}
                    offerChoiceRow={offerChoiceRow}
                    chooseSupplier={chooseSupplier}
                    maxPartScore={maxPartScore}
                />}

            {first &&
            <TableCell className={classes.scoreTotalColumn} rowSpan={rowSpan}>


                {isAdditionalOffers && <OfferChoiceSummaryCellAdditionalRound
                    demandId={demandId}
                    working={working}
                    rowWorking={curWorking}
                    setRowWorking={setCurWorking}
                    editable={editable}
                    refreshRow={refreshRow}
                    serverBrowserTimeDiff={serverBrowserTimeDiff}
                    roundName={offerChoiceRow.name}
                    additionalRoundId={offerChoiceRow.currentAdditionalRoundInfo.roundId}
                    additionalRoundStatus={offerChoiceRow.currentAdditionalRoundInfo.status}
                    additionalRoundEndDate={offerChoiceRow.currentAdditionalRoundInfo.endDate}
                    additionalRoundOpenOfferDate={offerChoiceRow.currentAdditionalRoundInfo.openOfferDate}
                />}

                {isAuction && <OfferChoiceSummaryCellAuction
                    demandId={demandId}
                    working={working}
                    rowWorking={curWorking}
                    setRowWorking={setCurWorking}
                    offerChoiceRow={offerChoiceRow}
                    editable={editable}
                    refreshRow={refreshRow}
                    serverBrowserTimeDiff={serverBrowserTimeDiff}
                />}

                {!isAdditionalOffers && !isAuction && <OfferChoiceSummaryCell
                    demandId={demandId}
                    rowWorking={curWorking}
                    setRowWorking={setCurWorking}
                    working={working}
                    offerChoiceRow={offerChoiceRow}
                    editable={editable}
                    chosenStatusDone={chosenStatusDone}
                    setChosenStatusDone={setChosenStatusDone}
                    chosenSuppliers={chosenSuppliers}
                    updateStatus={doUpdateStatus}
                    modifyChoice={doModifyChoice}
                />}
            </TableCell>
            }
        </TableRow>)
    }


    return (offerChoiceRow.offerChoices.length > 0 ?
            offerChoiceRow.offerChoices.map((element, index, array) => getRow(element, index, array))
            :
            getRow({identity: -1}, 0, [])
    )
}

OfferChoiceRow.propTypes = {
    demandId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    editable: PropTypes.bool.isRequired,
    working: PropTypes.bool.isRequired,
    offerChoiceRow: PropTypes.object.isRequired,
    rowNumber: PropTypes.number.isRequired,
    updateStatus: PropTypes.func.isRequired,
    modifyChoice: PropTypes.func.isRequired,
}

export default OfferChoiceRow;

